// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState, useEffect } from 'react';
import './LoadingScreen.css';

/**
 * Loading Screen Component
 * 
 * Shows loading animation with rotating tips while game initializes
 */
const LoadingScreen = ({ onComplete }) => {
  const [currentTip, setCurrentTip] = useState(0);
  const [progress, setProgress] = useState(0);

  const tips = [
    "💡 Higher warp factors consume more fuel but get you there faster",
    "💡 Refuel at planets and stations before long journeys",
    "💡 Explore planets to discover new alien species",
    "💡 Trade goods are cheaper at their source planets",
    "💡 Combat drains power - manage your weapon fire modes",
    "💡 Press F1 for help, F2 for reputation, F3 to reset",
    "💡 Deep scans reveal alien life and resources",
    "💡 Warp gates connect different universes",
    "💡 Combo hits in combat deal bonus damage",
    "💡 ACE status unlocks at 3+ combat victories",
    "💡 Agricultural planets have cheap food rations",
    "💡 Mining planets have cheap rare minerals",
    "💡 Install modules to expand ship capabilities",
    "💡 Reputation affects alien interactions",
    "💡 Save your game regularly at stations"
  ];

  useEffect(() => {
    // Rotate tips every 3 seconds
    const tipInterval = setInterval(() => {
      setCurrentTip(prev => (prev + 1) % tips.length);
    }, 3000);

    // Simulate loading progress
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          clearInterval(tipInterval);
          setTimeout(() => onComplete(), 500);
          return 100;
        }
        return prev + 2;
      });
    }, 50);

    return () => {
      clearInterval(tipInterval);
      clearInterval(progressInterval);
    };
  }, [onComplete, tips.length]);

  return (
    <div className="loading-screen">
      <div className="loading-content">
        <div className="loading-logo">
          <div className="loading-ship">🚀</div>
          <div className="loading-title">STARSHIP CONSOLE</div>
          <div className="loading-subtitle">Initializing Systems...</div>
        </div>

        <div className="loading-progress-container">
          <div className="loading-progress-bar">
            <div 
              className="loading-progress-fill" 
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="loading-progress-text">{progress}%</div>
        </div>

        <div className="loading-tip">
          <div className="loading-tip-content">
            {tips[currentTip]}
          </div>
        </div>

        <div className="loading-systems">
          <div className={`loading-system ${progress > 20 ? 'loaded' : ''}`}>
            ✓ Navigation Systems
          </div>
          <div className={`loading-system ${progress > 40 ? 'loaded' : ''}`}>
            ✓ Weapons Array
          </div>
          <div className={`loading-system ${progress > 60 ? 'loaded' : ''}`}>
            ✓ Life Support
          </div>
          <div className={`loading-system ${progress > 80 ? 'loaded' : ''}`}>
            ✓ Warp Drive
          </div>
        </div>
      </div>

      <div className="loading-stars">
        {[...Array(50)].map((_, i) => (
          <div 
            key={i} 
            className="loading-star"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default LoadingScreen;
